import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import AirplanemodeInactiveIcon from "@mui/icons-material/AirplanemodeInactive";
import { Box, Tooltip, Typography, styled } from "@mui/material";
import classNames from "classnames";
import moment from "moment-timezone";
import React, { memo } from "react";
import { IFlightPairingStatusRow } from "./pairingStatusTypes";

interface IFlightStatusChipProps {
  row: IFlightPairingStatusRow;
  selected?: boolean;
  highlighted?: boolean;
  /** Show the aircraft registration next to the icon (calendar cells are too narrow for it). */
  showRegistration?: boolean;
  onSelect?: (row: IFlightPairingStatusRow) => void;
  onContextMenu?: (row: IFlightPairingStatusRow, event: React.MouseEvent) => void;
}

/** Height of one chip plus its vertical margin; the calendar grid derives row heights from it. */
export const FLIGHT_STATUS_CHIP_HEIGHT = 22;

const Chip = styled("div")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  height: 18,
  minWidth: 18,
  margin: "2px 0px",
  padding: "0px 3px",
  borderRadius: 3,
  border: "2px solid transparent",
  boxSizing: "border-box",
  cursor: "pointer",
  color: theme.palette.common.black,
  fontSize: 10,
  fontWeight: 700,
  lineHeight: "14px",
  "& svg": { width: 12, height: 12, "& path": { fill: theme.palette.common.black } },
  "&.unassigned svg path": { fill: theme.palette.background.default },
  "&.no_pairing": { backgroundColor: theme.custom.pairingStatus.noPairingColor },
  "&.missing_crew": { backgroundColor: theme.custom.pairingStatus.missingCrewColor },
  "&.extra_crew": { backgroundColor: theme.custom.pairingStatus.extraCrewColor },
  "&.extra_pos": { backgroundColor: theme.custom.pairingStatus.extraPosColor },
  "&.missing_ac_info": { backgroundColor: theme.custom.pairingStatus.missingACInfoColor },
  "&.ok": { backgroundColor: theme.custom.pairingStatus.okColor },
  "&:hover, &:focus-visible, &.highlighted": { borderColor: theme.palette.secondary.contrastText, outline: "none" },
  "&.selected": { borderColor: theme.palette.secondary.main },
}));

const positionList = (items: { positionCode: string; count: number }[]): string =>
  items.map((i) => (i.count > 1 ? `${i.positionCode} x${i.count}` : i.positionCode)).join(", ");

export const FlightStatusTooltip = ({ row }: { row: IFlightPairingStatusRow }): JSX.Element => {
  const out = row.actualOut ?? row.scheduledOut;
  const { crewSummary, status } = row;
  return (
    <Box sx={{ fontSize: 12 }}>
      <Typography variant="subtitle2" component="div">
        {row.flightNumber}
        {row.flightNumberSuffix ?? ""} {row.origin}-{row.destination} · {moment.utc(out).format("DD MMM HH:mm")}z
      </Typography>
      <div>
        <b>{status?.label}</b>: {status?.message}
      </div>
      <div>Aircraft: {row.aircraft?.registration ?? "Unassigned"}</div>
      <div>Pairings: {row.pairingShortCodes?.length ? row.pairingShortCodes.join(", ") : "none"}</div>
      {crewSummary && (
        <div>
          Crew: {crewSummary.assigned}/{crewSummary.required}
          {crewSummary.missing?.length > 0 && <> · missing {positionList(crewSummary.missing)}</>}
          {crewSummary.extraCrew?.length > 0 && <> · extra {positionList(crewSummary.extraCrew)}</>}
          {crewSummary.extraPositions?.length > 0 && <> · extra position {positionList(crewSummary.extraPositions)}</>}
        </div>
      )}
      <div style={{ opacity: 0.7 }}>Click or press Enter for details</div>
    </Box>
  );
};

const FlightStatusChipComponent = (props: IFlightStatusChipProps): JSX.Element => {
  const { row, selected, highlighted, showRegistration, onSelect, onContextMenu } = props;
  const registration = row.aircraft?.registration;
  return (
    <Tooltip enterDelay={300} placement="top" title={<FlightStatusTooltip row={row} />}>
      <Chip
        data-testid={`fps-chip-${row.flightId}`}
        role="button"
        tabIndex={0}
        aria-label={`${row.flightNumber} ${row.origin}-${row.destination} ${row.status?.label ?? ""}`}
        aria-pressed={!!selected}
        className={classNames(
          "flight_pairing_status",
          row.status?.className,
          highlighted && "highlighted",
          selected && "selected",
          !registration && "unassigned"
        )}
        onClick={(event) => {
          event.stopPropagation();
          onSelect?.(row);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            onSelect?.(row);
          }
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          (onContextMenu ?? onSelect)?.(row, event);
        }}
      >
        {registration ? <AirplanemodeActiveIcon /> : <AirplanemodeInactiveIcon />}
        {showRegistration && registration && <span>{registration}</span>}
      </Chip>
    </Tooltip>
  );
};

export const FlightStatusChip = memo(FlightStatusChipComponent);
